import {atom} from 'jotai';
import {getMaterials} from '@/actions/materials';
import {hybridSearch} from '@/actions/products/hybrid-search';
import type {
    MaterialsState,
    MaterialsSearchParams,
    Material,
} from '@/types/materials';

/**
 * Adapter to transform hybrid search product data to Material-like objects.
 * This is a temporary solution - ideally we'd have a dedicated material search.
 *
 * Note: Hybrid search returns product data, not actual materials. This adapter creates
 * minimal Material objects for display purposes only. Missing required fields are
 * populated with placeholder values.
 */
function adaptProductDataToMaterials(
    productData: Array<{id: string; name?: string; sku?: string}>
): Material[] {
    const now = new Date().toISOString();
    return productData.map((item) => ({
        id: parseInt(item.id, 10) || 0,
        kind: 'finish', // Default kind for search results
        sku: item.sku ?? item.id,
        name: item.name ?? 'Unknown',
        properties: [],
        created_at: now,
        updated_at: now,
        supplier_id: 0,
    }));
}

const initialState: MaterialsState = {
    items: [],
    loading: false,
    error: null,
    initialized: false,
    pagination: {
        currentPage: 1,
        totalPages: 0,
        totalCount: 0,
    },
};

export const materialsAtom = atom<MaterialsState>(initialState);
export const materialsSearchParamsAtom = atom<MaterialsSearchParams>({
    page: 1,
    per_page: 20,
    sort_by: 'sku',
    sort_order: 'asc',
});

export const aiMaterialsSearchQueryAtom = atom<string>('');
export const getMaterialsAtom = atom(null, (get, set) =>
    getMaterials(get, set)
);

export const searchMaterialsAtom = atom(null, (get, set, query: string) => {
    set(materialsSearchParamsAtom, (prev: MaterialsSearchParams) => ({
        ...prev,
        q: query,
        page: 1,
    }));

    return getMaterials(get, set);
});

export const filterMaterialsByKindAtom = atom(
    null,
    (get, set, kind: string) => {
        set(materialsSearchParamsAtom, (prev: MaterialsSearchParams) => ({
            ...prev,
            kind,
            page: 1,
        }));

        return getMaterials(get, set);
    }
);

export const filterMaterialsByPropertyAtom = atom(
    null,
    (get, set, propertyValueId: number) => {
        set(materialsSearchParamsAtom, (prev: MaterialsSearchParams) => {
            const prevValue = String(prev.properties ?? '');
            const newValue = String(propertyValueId);

            if (prevValue === newValue) {
                return {
                    ...prev,
                    properties: '',
                    page: 1,
                };
            }

            return {...prev, properties: newValue, page: 1};
        });

        return getMaterials(get, set);
    }
);

export const performAiMaterialsSearchAtom = atom(
    null,
    async (_get, set, query: string) => {
        if (!query.trim()) return set(aiMaterialsSearchQueryAtom, '');

        try {
            set(materialsAtom, (prev: MaterialsState) => ({
                ...prev,
                loading: true,
            }));

            set(aiMaterialsSearchQueryAtom, query);

            // Auth is handled via cookies in hybridSearch
            const result = await hybridSearch(query);

            if (result.products && result.products.length > 0) {
                set(materialsAtom, (prev: MaterialsState) => ({
                    ...prev,
                    items: adaptProductDataToMaterials(result.products),
                    loading: false,
                    initialized: true,
                }));
            } else {
                set(materialsAtom, (prev: MaterialsState) => ({
                    ...prev,
                    items: [],
                    loading: false,
                    initialized: true,
                }));
            }

            return result;
        } catch (error) {
            set(materialsAtom, (prev: MaterialsState) => ({
                ...prev,
                loading: false,
                error: error instanceof Error ? error.message : 'Search failed',
            }));
        }
    }
);

export const selectedMaterialAtom = atom<Material | null>(null);
export const selectedMaterialsAtom = atom<Material[]>([]);
